// src/pages/autopilot/IntentInput.js
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useDispatch, useSelector } from 'react-redux';
import { setUserIntent, setLoading, setError, setGeneratedICP } from '../../store/slices/autoPilotSlice';
import { generateICP } from '../../services/autoPilotService';

function IntentInput({ onNext }) {
  const dispatch = useDispatch();
  const { userIntent, loading, error } = useSelector((state) => state.autoPilot);
  const [localIntent, setLocalIntent] = useState(userIntent || '');

  const exampleIntents = [
    'find VP of sales currently working for Walmart in Bay Area for Camera category',
    'find marketing directors at tech startups in San Francisco with 5+ years experience',
    'find product managers at Fortune 500 companies in New York with SaaS experience'
  ];

  const handleSubmit = async () => {
    if (!localIntent.trim()) {
      dispatch(setError('Please enter your search intent'));
      return;
    }

    dispatch(setLoading(true));
    dispatch(setError(null));
    dispatch(setUserIntent(localIntent));

    try {
      const response = await generateICP(localIntent);
      if (response.status === 'success' && response.data) {
        dispatch(setGeneratedICP(response.data));
        if (onNext) {
          onNext();
        }
      } else {
        dispatch(setError(response.message || 'Failed to generate ICP. Please try again.'));
      }
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'An error occurred. Please try again.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleExampleClick = (example) => {
    setLocalIntent(example);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Enter Your Search Intent
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
        Describe what you're looking for in natural language. Our AI will generate an Ideal Customer Profile (ICP) based on your requirements.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(setError(null))}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 3, mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={localIntent}
            onChange={(e) => setLocalIntent(e.target.value)}
            placeholder="Example: find VP of sales currently working for Walmart in Bay Area for Camera category"
            variant="outlined"
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !localIntent.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              bgcolor: '#1BE885',
              color: '#000',
              fontWeight: 600,
              '&:hover': { bgcolor: '#20ec84' },
              '&:disabled': { bgcolor: '#ccc' }
            }}
          >
            {loading ? 'Generating ICP...' : 'Generate ICP'}
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Example Intents
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {exampleIntents.map((example, index) => (
              <Button
                key={index}
                variant="outlined"
                onClick={() => handleExampleClick(example)}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  py: 1.5,
                  px: 2,
                  borderColor: '#1BE885',
                  color: '#1BE885',
                  '&:hover': {
                    borderColor: '#20ec84',
                    bgcolor: '#f0fdf4'
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "{example}"
                </Typography>
              </Button>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default IntentInput;
